import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    OnInit,
    signal,
    WritableSignal,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom, Observable } from 'rxjs';

import { FieldInterventionEntityBloc } from '@connect-services/client/blocs/field-intervention-entity.bloc';
import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { ComplexModalTemplate } from '@connect-services/client/components/complexe-modal/complex-modal-template.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import {
    FieldInterventionForm,
    FieldInterventionFormControllerGet200Response,
    FieldInterventionFormsService,
    PeopleService,
    Person,
    PersonControllerGetRequestParams,
} from '@connect-services/client/sdk/api';
import { FetchAllService } from '@connect-services/client/services/fetch-all.service';
import { FieldInterventionService } from '@connect-services/client/services/field-intervention.service';
import { CheckboxState } from '@connect-services/src/lib/components/checkbox/checkbox.component';
import { SelectComponent, SelectOption } from '@connect-services/src/lib/components/select/select.component';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { ProgressBarComponent } from '@shared/client/components/progress-bar/progress-bar.component';
import {
    defaultPaginationParameters,
    paginationMaxTake,
    PaginationParameters,
} from '@shared/client/models/pagination.model';

export type FieldInterventionEntityAttributions = {
    forms: FieldInterventionForm[];
    people: Person[];
    removedForms: FieldInterventionForm[];
    removedPeople: Person[];
};

export type FieldInterventionPlanningModalData = {
    hasLoader: boolean;
    isIntermediateStateEnabled: boolean;
    numberOfEntities: number;
    preselectedFormCheckState: {
        [key: string]: SelectState;
    };
    preselectedFormIds: string[];
    preselectedPeopleCheckState: {
        [key: string]: SelectState;
    };
    preselectedPeopleIds: string[];
    startPlanning: (
        attributions: FieldInterventionEntityAttributions,
        report?: (delta: number) => void,
    ) => Promise<void>;
    updateMode: boolean;
};

export enum FieldInterventionPlanningModalStepEnum {
    FORM = 1,
    IN_PROGRESS = 2,
}

export type SelectState = {
    canBeUnchecked: boolean;
    checkState: CheckboxState;
    isEnabled: boolean;
};

type OptionAction<T> = {
    disabledReason: string;
    fetch: () => Promise<T[]>;
    options: WritableSignal<SelectOption<T>[]>;
    preselectEntities: string[];
    preselectState: Record<string, SelectState>;
    selectedOptions: WritableSignal<SelectOption<T>[]>;
};

type OptionActionMap = { [P in keyof OptionTypeMap]: OptionAction<OptionTypeMap[P]> };

export enum AttributionTypeEnum {
    FORM = 'form',
    PEOPLE = 'people',
}

type AttributionChanges<T> = {
    removed: T[];
    selected: T[];
};

interface OptionTypeMap {
    form: FieldInterventionForm;
    people: Person;
}

export const PROGRESS_BAR_MINIMUM_ENTITY_COUNT = 50;

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ComplexModalTemplate, ProgressBarComponent, RessourcesModule, SelectComponent],
    selector: 'app-field-intervention-planning-modal',
    templateUrl: './field-intervention-planning-modal.component.html',
})
export class FieldInterventionPlanningModalComponent implements OnInit {
    protected readonly currentStep = signal<FieldInterventionPlanningModalStepEnum>(
        FieldInterventionPlanningModalStepEnum.FORM,
    );
    protected readonly entitiesLoaded = signal<number>(0);
    protected readonly formOptions = signal<SelectOption<FieldInterventionForm>[]>([]);
    protected readonly isProgressBarNeeded = computed<boolean>(
        () => this.data.numberOfEntities >= PROGRESS_BAR_MINIMUM_ENTITY_COUNT,
    );
    protected readonly peopleOptions = signal<SelectOption<Person>[]>([]);
    protected readonly selectedFormOptions = signal<SelectOption<FieldInterventionForm>[]>([]);
    protected readonly selectedPeopleOptions = signal<SelectOption<Person>[]>([]);
    protected readonly updatedAttributionIds = signal<Record<AttributionTypeEnum, Set<string>>>({
        [AttributionTypeEnum.FORM]: new Set(),
        [AttributionTypeEnum.PEOPLE]: new Set(),
    });

    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected readonly AttributionTypeEnum = AttributionTypeEnum;
    protected data: FieldInterventionPlanningModalData = inject(MAT_DIALOG_DATA);
    protected dialogRef = inject(MatDialogRef<FieldInterventionPlanningModalComponent>);
    protected readonly FieldInterventionPlanningModalStepEnum = FieldInterventionPlanningModalStepEnum;

    private readonly fieldInterventionAttributions = computed<FieldInterventionEntityAttributions>(() => {
        const formChanges = this.getAttributionChanges(
            this.formOptions(),
            this.selectedFormOptions(),
            this.updatedAttributionIds()[AttributionTypeEnum.FORM],
        );

        const peopleChanges = this.getAttributionChanges(
            this.peopleOptions(),
            this.selectedPeopleOptions(),
            this.updatedAttributionIds()[AttributionTypeEnum.PEOPLE],
        );

        return {
            forms: formChanges.selected,
            people: peopleChanges.selected,
            removedForms: formChanges.removed,
            removedPeople: peopleChanges.removed,
        };
    });

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly fetchAllService = inject(FetchAllService);
    private readonly fieldInterventionEntityBloc = inject(FieldInterventionEntityBloc);
    private readonly fieldInterventionService = inject(FieldInterventionService);
    private readonly formsService = inject(FieldInterventionFormsService);
    private readonly optionActionByType: OptionActionMap;
    private readonly peopleService = inject(PeopleService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly translocoService = inject(TranslocoService);

    public constructor() {
        this.optionActionByType = {
            form: {
                disabledReason: 'cannotDeselectForm',
                fetch: (): Promise<FieldInterventionForm[]> => this.fetchForms(),
                options: this.formOptions,
                preselectEntities: this.data.preselectedFormIds,
                preselectState: this.data.preselectedFormCheckState,
                selectedOptions: this.selectedFormOptions,
            },
            people: {
                disabledReason: 'cannotUnassignParticipant',
                fetch: (): Promise<Person[]> => this.fetchPeople(),
                options: this.peopleOptions,
                preselectEntities: this.data.preselectedPeopleIds,
                preselectState: this.data.preselectedPeopleCheckState,
                selectedOptions: this.selectedPeopleOptions,
            },
        };

        effect(async () => {
            if (this.currentStep() !== FieldInterventionPlanningModalStepEnum.IN_PROGRESS) {
                return;
            }

            await this.data.startPlanning(this.fieldInterventionAttributions(), (delta: number) =>
                this.entitiesLoaded.update((count: number) => count + delta),
            );

            await this.refreshDetailPanel();

            this.dialogRef.close(ModalResultEnum.CONFIRM);
        });
    }

    public async closeModal(event: ModalResultEnum): Promise<void> {
        if (event === ModalResultEnum.CANCEL) {
            this.dialogRef.close();

            return;
        }

        if (!this.data.hasLoader) {
            await this.data.startPlanning(this.fieldInterventionAttributions(), () => {});
            this.dialogRef.close(ModalResultEnum.CONFIRM);
        }

        await this.refreshDetailPanel();
    }

    public async ngOnInit(): Promise<void> {
        await this.setupOptions('people');
        await this.setupOptions('form');
    }

    protected onOptionToggled<T extends { id: string }>(option: SelectOption<T>, type: AttributionTypeEnum): void {
        this.updatedAttributionIds.update((currentValue: Record<AttributionTypeEnum, Set<string>>) => ({
            ...currentValue,
            [type]: new Set(currentValue[type]).add(option.value.id),
        }));
    }

    private async fetchEntities<T extends { id: string }>(
        apiCallMethod: (params: PaginationParameters) => Observable<{ hasNextPage: boolean; items: T[] }>,
        array: T[],
    ): Promise<void> {
        let hasNextPage = false;
        const paginationParameters: PaginationParameters = { ...defaultPaginationParameters, take: paginationMaxTake };

        do {
            const paginatedResponse = await firstValueFrom(apiCallMethod(paginationParameters));
            array.push(...paginatedResponse.items);
            hasNextPage = paginatedResponse.hasNextPage;
            paginationParameters.page += 1;
        } while (hasNextPage);
    }

    private async fetchForms(): Promise<FieldInterventionForm[]> {
        const forms: FieldInterventionForm[] = [];
        await this.fetchEntities<FieldInterventionForm>(
            (params): Observable<FieldInterventionFormControllerGet200Response> =>
                this.formsService.fieldInterventionFormControllerGet(params),
            forms,
        );

        return forms;
    }

    private fetchPeople(): Promise<Person[]> {
        return this.fetchAllService.getAll<PersonControllerGetRequestParams, Person>(
            this.peopleService.personControllerGet.bind(this.peopleService),
            { project: this.projectBloc.value()!.id, take: paginationMaxTake },
        );
    }

    private getAttributionChanges<T extends { id: string }>(
        options: SelectOption<T>[],
        selectedOptions: SelectOption<T>[],
        updatedIds: Set<string>,
    ): AttributionChanges<T> {
        const selected = selectedOptions.map((option: SelectOption<T>) => option.value);
        const selectedIds = new Set(selected.map((item: T) => item.id));

        const removed = options
            .filter((option: SelectOption<T>) => updatedIds.has(option.value.id) && !selectedIds.has(option.value.id))
            .map((option: SelectOption<T>) => option.value);

        return { removed, selected };
    }

    private async refreshDetailPanel(): Promise<void> {
        const entityId: string | null = this.fieldInterventionService.getDetailPanelUuidFromUrl();

        if (!entityId) {
            return;
        }

        await this.fieldInterventionEntityBloc.load({ id: entityId, onlyBookmarked: true }, { refresh: true });
    }

    private async setupOptions<K extends keyof OptionTypeMap>(type: K): Promise<void> {
        if (!this.optionActionByType) {
            return;
        }

        const config = this.optionActionByType[type];
        const optionEntities = await config.fetch();

        if (!optionEntities.length) {
            config.selectedOptions.set([]);

            return;
        }

        const options: SelectOption<OptionTypeMap[K]>[] = optionEntities.map((entity: OptionTypeMap[K]) => {
            const state = config.preselectState?.[entity.id];

            const isDefaultSelected =
                config.preselectEntities.includes(entity.id) && state?.checkState === CheckboxState.CHECKED;

            const label = 'fullName' in entity ? entity.fullName : entity.name;
            const uncheckState = !state || state.canBeUnchecked ? CheckboxState.UNCHECKED : CheckboxState.INDETERMINATE;

            return {
                ...(this.data.isIntermediateStateEnabled &&
                    state && {
                        defaultCheckState: state.checkState,
                    }),
                ...(state &&
                    !state.isEnabled && { disabledReason: this.translocoService.translate(config.disabledReason) }),
                defaultSelected: isDefaultSelected,
                label,
                uncheckState,
                value: entity,
            };
        });

        config.options.set(options);

        const selected: SelectOption<OptionTypeMap[K]>[] = options.filter((option: SelectOption<OptionTypeMap[K]>) => {
            const entityId = option.value.id;

            return (
                config.preselectEntities.includes(entityId) &&
                config.preselectState?.[entityId]?.checkState === CheckboxState.CHECKED
            );
        });

        config.selectedOptions.set(selected);
    }
}
